import { Component, OnInit, Renderer2 } from "@angular/core";
import { ThemeService } from "../../theme.service";
import { ChatSession, ChatService } from "../../services/chat.service";
import { AuthService } from "../../services/auth.service";
import { Router } from "@angular/router";
import { environment } from "../../../environments/environment";
import {
  getStudentThemeVisual,
  isJuniorStudentTheme,
  normalizeStudentThemeName
} from "../student-theme.util";

@Component({
  selector: 'app-student-layout',
  templateUrl: './student-layout.component.html',
  styleUrls: ['./student-layout.component.css']
})
export class StudentLayoutComponent implements OnInit {
  readonly enableStudentThemes = environment.enableStudentThemes ?? false;
  // Keep the main chat layout spacious by default:
  // - left sidebar expanded
  // - notes sidebar collapsed (can be toggled open)
  isSidebarOpen = true;
  isNotesSidebarOpen = false;
  selectedChat: ChatSession | null = null;
  userName = "Mark";
  user: any;
  primaryColor = "";
  isCustomTheme = false;
  showScienceDecor = false;
  themeName = "default";
  layoutThemeClass = "student-layout-default";
  themeBadgeClass = "theme-badge-default";
  studentGrade: number | null = null;
  showThemePopup = false;
  pendingThemeName = "default";
  readonly juniorThemeOptions = [
    { key: 'default', label: 'None', badgeClass: 'theme-badge-none' },
    { key: 'rainbow', label: 'Rainbow', badgeClass: 'theme-badge-rainbow' },
    { key: 'rocket', label: 'Rocket', badgeClass: 'theme-badge-rocket' },
    { key: 'mango', label: 'Mango', badgeClass: 'theme-badge-mango' },
    { key: 'ice_cream', label: 'Ice Cream', badgeClass: 'theme-badge-ice-cream' }
  ];

  constructor(
    private chatService: ChatService,
    private themeService: ThemeService,
    private router: Router,
    private renderer: Renderer2,
    private authService: AuthService
  ) {
    this.user = this.authService.getUser();
    if (this.user?.user?.fullName) {
      this.userName = this.user.user.fullName.split(' ')[0];
    }
    if (this.user?.user?.theme?.customTheme?.primaryColor) {
      this.primaryColor = this.user.user.theme.customTheme.primaryColor;
    }
  }

  ngOnInit(): void {
    this.isCustomTheme = this.user?.user?.theme?.name === 'custom';
    const grade = Number(this.user?.user?.studentClass?.grade);
    this.studentGrade = Number.isNaN(grade) ? null : grade;
    if (this.enableStudentThemes) {
      this.themeName = normalizeStudentThemeName(this.user?.user?.theme?.name);
      this.showScienceDecor = this.studentGrade !== null && this.studentGrade > 6 && !this.isJuniorClassStudent();
      this.layoutThemeClass = getStudentThemeVisual(this.themeName).layoutClass;
      this.themeBadgeClass = this.getThemeBadgeClass(this.themeName);
      this.pendingThemeName = this.isJuniorTheme() ? this.themeName : 'default';
      this.isNotesSidebarOpen = true;
    } else {
      this.themeName = 'default';
      this.showScienceDecor = false;
      this.layoutThemeClass = 'student-layout-default';
      this.themeBadgeClass = 'theme-badge-none';
      this.pendingThemeName = 'default';
    }
  }

  toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
    console.log('Sidebar toggled:', this.isSidebarOpen);
  }

  closeSidebar(): void {
    this.isSidebarOpen = false;
  }

  toggleNotesSidebar(): void {
    this.isNotesSidebarOpen = !this.isNotesSidebarOpen;
    console.log('Notes Sidebar toggled:', this.isNotesSidebarOpen);
  }

  closeNotesSidebar(): void {
    this.isNotesSidebarOpen = false;
  }

  onChatSelected(chat: ChatSession): void {
    this.selectedChat = chat;
    this.router.navigate(['/student/chat', chat.sessionId]);
    this.closeSidebar();
  }

  onNewChatRequested(): void {
    this.selectedChat = null;
    this.chatService.setCurrentChat(null);
    this.router.navigate(['/student/chat']);
    this.closeSidebar();
  }

  isDarkTheme(): boolean {
    return this.themeService.getCurrentTheme();
  }

  toggleTheme(): void {
    if (!this.enableStudentThemes) {
      return;
    }
    this.themeService.toggleTheme();
  }

  openThemePopup(): void {
    if (!this.enableStudentThemes || !this.isJuniorClassStudent()) {
      return;
    }
    this.pendingThemeName = this.isJuniorTheme() ? this.themeName : 'default';
    this.showThemePopup = true;
  }

  closeThemePopup(): void {
    this.showThemePopup = false;
  }

  selectPopupTheme(themeKey: string): void {
    this.pendingThemeName = themeKey;
  }

  saveSelectedTheme(): void {
    if (!this.enableStudentThemes || !this.user?.user) {
      this.closeThemePopup();
      return;
    }

    const savedThemeName = this.pendingThemeName === 'default' ? 'default' : this.pendingThemeName;
    const updatedUser = {
      ...this.user,
      user: {
        ...this.user.user,
        theme: {
          ...this.user.user.theme,
          name: savedThemeName
        }
      }
    };

    localStorage.setItem('user', JSON.stringify(updatedUser));
    this.user = updatedUser;
    this.themeName = normalizeStudentThemeName(savedThemeName);
    this.layoutThemeClass = getStudentThemeVisual(this.themeName).layoutClass;
    this.themeBadgeClass = this.getThemeBadgeClass(this.themeName);
    this.closeThemePopup();
    window.location.reload();
  }

  goToProfile(): void {
    this.router.navigate(['/student/profile']);
  }

  getInitials(name: string): string {
    if (!name) return '';
    const nameParts = name.trim().split(' ');
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
    return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
  }

  isJuniorTheme(): boolean {
    return isJuniorStudentTheme(this.themeName);
  }

  isJuniorClassStudent(): boolean {
    return this.studentGrade !== null && this.studentGrade <= 6;
  }

  getThemeBadgeClass(themeKey: string): string {
    return themeKey === 'default'
      ? 'theme-badge-none'
      : `theme-badge-${themeKey.replace(/_/g, '-')}`;
  }
}
